import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';

import { canViewInsightSurface } from '../../config/insightAccess';
import { useAuth } from '../../contexts/AuthContext';
import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import {
  loadInsightStripHidden,
  saveInsightStripHidden,
} from '../../core/storage/insightStripStorage';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { listInsights, type InsightCard } from '../../services/assistantService';
import { subscribeDashboardRefresh } from '../../services/dashboardRefreshBus';
import {
  ensureInsightsGeneratedOnce,
  insightRefreshKey,
  invalidateInsightRefresh,
} from '../../services/insightRefreshGate';
import { ErrorRetry } from '../ui/ErrorRetry';
import { RefreshButton } from '../ui/RefreshButton';
import { InsightExpandableCard } from './InsightExpandableCard';

const STRIP_LIMIT = 3;

function primaryWidgetOf(card: InsightCard): string {
  if (card.primaryWidget) return card.primaryWidget;
  const targets = card.widgetTargets?.filter((t) => t && t !== 'home_strip') ?? [];
  return targets[0] ?? 'home_spend';
}

/**
 * Home AI insights — hideable section. Title on top, OIIA inside each card.
 */
export function InsightStrip() {
  const { isAuthenticated, user } = useAuth();
  const { view, viewReady } = useHouseholdMembership();
  const [insights, setInsights] = useState<InsightCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [prefReady, setPrefReady] = useState(false);
  const requestIdRef = useRef(0);

  const isPro = user?.plan === 'pro';
  const canView = canViewInsightSurface('home_strip', isPro);

  useEffect(() => {
    let cancelled = false;
    void loadInsightStripHidden().then((value) => {
      if (cancelled) return;
      setHidden(value);
      setPrefReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleHidden = () => {
    setHidden((prev) => {
      const next = !prev;
      void saveInsightStripHidden(next);
      return next;
    });
  };

  const load = useCallback(async () => {
    if (!isAuthenticated || !canView || !viewReady) {
      if (!isAuthenticated || !canView) setInsights([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setGenerating(false);
    setError(null);
    try {
      const key = insightRefreshKey(user?.id, view);
      // List first — POST /insights/refresh can 29s-timeout on API Gateway after
      // a clean login and was surfacing as "Failed to fetch" on the strip.
      const res = await listInsights(view, { widget: 'home_strip' });
      if (requestId !== requestIdRef.current) return;
      const cards = (res.insights ?? [])
        .filter((c) => c.featuredOnStrip !== false)
        .slice(0, STRIP_LIMIT);
      setInsights(cards);
      setError(null);

      if (cards.length === 0) {
        setGenerating(true);
        void (async () => {
          await ensureInsightsGeneratedOnce(key);
          if (requestId !== requestIdRef.current) return;
          try {
            const next = await listInsights(view, { widget: 'home_strip' });
            if (requestId !== requestIdRef.current) return;
            setInsights(
              (next.insights ?? [])
                .filter((c) => c.featuredOnStrip !== false)
                .slice(0, STRIP_LIMIT),
            );
          } catch {
            // Empty list already succeeded — don't surface generate failures.
          } finally {
            if (requestId === requestIdRef.current) setGenerating(false);
          }
        })();
      } else {
        setGenerating(false);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setInsights([]);
      setError(err instanceof Error ? err.message : 'Could not load insights');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [canView, isAuthenticated, view, viewReady, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return subscribeDashboardRefresh(() => {
      invalidateInsightRefresh('insight_strip');
      void load();
    });
  }, [load]);

  if (!isAuthenticated || !canView || !viewReady || !prefReady) return null;
  if (!loading && !generating && insights.length === 0 && !error) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.headingRow}>
        <Text style={styles.heading}>AI insights</Text>
        <View style={styles.headingActions}>
          <RefreshButton onPress={() => void load()} refreshing={loading} compact />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={hidden ? 'Show AI insights' : 'Hide AI insights'}
            onPress={toggleHidden}
            style={({ pressed }) => [styles.toggle, pressed && styles.pressed]}
          >
            <Text style={styles.toggleText}>{hidden ? 'Show' : 'Hide'}</Text>
          </Pressable>
        </View>
      </View>
      {hidden ? (
        <Text style={styles.hiddenHint}>Hidden — insights stay on widgets and in chat.</Text>
      ) : (loading || generating) && insights.length === 0 ? (
        <View style={styles.generating}>
          <ActivityIndicator color={colors.accent} />
          {generating ? (
            <Text style={styles.hiddenHint}>Generating insights from your spend…</Text>
          ) : null}
        </View>
      ) : error && insights.length === 0 ? (
        <ErrorRetry message={error} onRetry={() => void load()} refreshing={loading} compact />
      ) : (
        <View style={styles.stack}>
          {insights.map((card, index) => (
            <InsightExpandableCard
              key={card.id}
              card={card}
              widget={primaryWidgetOf(card)}
              defaultExpanded={index === 0}
            />
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { marginBottom: spacing.lg, gap: spacing.sm },
  headingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  headingActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  heading: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  toggle: {
    paddingVertical: 4,
    paddingHorizontal: 2,
    cursor: 'pointer',
  } as ViewStyle,
  toggleText: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  hiddenHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  generating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  stack: {
    gap: spacing.sm,
  },
  pressed: { opacity: 0.85 },
});
